import { useState, useEffect } from 'react';
import axios from 'axios';
import { Gift, Snowflake, Ghost, Sun, Tag, Zap, Sparkles } from 'lucide-react';
import { API } from '../config';
import { useI18n } from '../context/I18nContext';

const THEME_CONFIG = {
  kickstarter: {
    bg: 'bg-gradient-to-r from-emerald-950/80 via-teal-900/60 to-emerald-950/80',
    border: 'border-emerald-500/30',
    accent: 'text-emerald-400',
    icon: Sparkles,
    decorations: false,
    decoClass: '',
  },
  christmas: {
    bg: 'bg-gradient-to-r from-red-950/80 via-red-900/60 to-green-950/80',
    border: 'border-red-500/30',
    accent: 'text-red-400',
    icon: Gift,
    decorations: true,
    decoClass: 'promo-christmas',
  },
  halloween: {
    bg: 'bg-gradient-to-r from-orange-950/80 via-orange-900/60 to-purple-950/80',
    border: 'border-orange-500/30',
    accent: 'text-orange-400',
    icon: Ghost,
    decorations: true,
    decoClass: 'promo-halloween',
  },
  black_friday: {
    bg: 'bg-gradient-to-r from-zinc-950 via-zinc-900 to-zinc-950',
    border: 'border-white/20',
    accent: 'text-white',
    icon: Tag,
    decorations: false,
    decoClass: 'promo-blackfriday',
  },
  cyber_monday: {
    bg: 'bg-gradient-to-r from-cyan-950/80 via-blue-950/80 to-cyan-950/80',
    border: 'border-cyan-500/30',
    accent: 'text-cyan-400',
    icon: Zap,
    decorations: true,
    decoClass: 'promo-cyber',
  },
  summer_sales: {
    bg: 'bg-gradient-to-r from-orange-950/70 via-amber-950/60 to-yellow-950/70',
    border: 'border-orange-500/30',
    accent: 'text-orange-400',
    icon: Sun,
    decorations: false,
    decoClass: 'promo-summer',
  },
  winter_sales: {
    bg: 'bg-gradient-to-r from-blue-950/80 via-slate-900/60 to-blue-950/80',
    border: 'border-blue-400/30',
    accent: 'text-blue-400',
    icon: Snowflake,
    decorations: true,
    decoClass: 'promo-winter',
  },
};

const DEFAULT_THEME = {
  bg: 'bg-gradient-to-r from-blue-950/70 via-violet-950/60 to-blue-950/70',
  border: 'border-blue-500/30',
  accent: 'text-blue-400',
  icon: Sparkles,
  decorations: false,
  decoClass: '',
};

export function PromoBanner({ variant }) {
  const [promos, setPromos] = useState([]);
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const { lang } = useI18n();

  useEffect(() => {
    axios.get(`${API}/promo/active-and-scheduled`).then(r => {
      const all = r.data.promotions || [];
      if (all.length > 0) setPromos(all);
    }).catch(() => {
      axios.get(`${API}/promo/active`).then(r => {
        if (r.data.promotions?.length > 0) setPromos(r.data.promotions);
      }).catch(() => {});
    });
  }, []);

  const promo = promos[0];

  useEffect(() => {
    if (!promo) return;
    const tick = () => {
      const now = new Date();
      const startDate = new Date(promo.start_date);
      const endDate = new Date(promo.end_date);
      const isScheduled = promo.status === 'scheduled' || startDate > now;
      const targetDate = isScheduled ? startDate : endDate;
      const diff = Math.max(0, targetDate.getTime() - now.getTime());
      setTimeLeft({
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff / 3600000) % 24),
        minutes: Math.floor((diff / 60000) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      });
    };
    tick();
    const i = setInterval(tick, 1000);
    return () => clearInterval(i);
  }, [promo]);

  if (!promo) return null;

  const templateKey = Object.keys(THEME_CONFIG).find(k => promo.name?.toLowerCase().includes(k.replace('_', ' ')) || promo.id?.includes(k));
  const theme = THEME_CONFIG[templateKey] || DEFAULT_THEME;
  const Icon = theme.icon;

  const now = new Date();
  const startDate = new Date(promo.start_date);
  const isScheduled = promo.status === 'scheduled' || startDate > now;
  const isActive = !isScheduled;

  const timerLabels = {
    en: { starts: 'Starts in', ends: 'Ends in', soon: 'Soon', d: 'days', h: 'hrs', m: 'min', s: 'sec', upto: 'Up to' },
    fr: { starts: 'Commence dans', ends: 'Se termine dans', soon: 'Bientôt', d: 'jrs', h: 'hrs', m: 'min', s: 'sec', upto: "Jusqu'à" },
    es: { starts: 'Empieza en', ends: 'Termina en', soon: 'Pronto', d: 'días', h: 'hrs', m: 'min', s: 'seg', upto: 'Hasta' },
    de: { starts: 'Startet in', ends: 'Endet in', soon: 'Bald', d: 'Tage', h: 'Std', m: 'Min', s: 'Sek', upto: 'Bis zu' },
    it: { starts: 'Inizia tra', ends: 'Finisce tra', soon: 'Presto', d: 'gg', h: 'ore', m: 'min', s: 'sec', upto: 'Fino a' },
    pt: { starts: 'Começa em', ends: 'Termina em', soon: 'Em breve', d: 'dias', h: 'hrs', m: 'min', s: 'seg', upto: 'Até' },
    nl: { starts: 'Start over', ends: 'Eindigt over', soon: 'Binnenkort', d: 'dgn', h: 'uur', m: 'min', s: 'sec', upto: 'Tot' },
    pl: { starts: 'Zaczyna się za', ends: 'Kończy się za', soon: 'Wkrótce', d: 'dni', h: 'god', m: 'min', s: 'sek', upto: 'Do' },
    tr: { starts: 'Başlıyor', ends: 'Bitiyor', soon: 'Yakında', d: 'gün', h: 'sa', m: 'dk', s: 'sn', upto: 'Kadar' },
    ru: { starts: 'Начнётся через', ends: 'Заканчивается через', soon: 'Скоро', d: 'дн', h: 'ч', m: 'мин', s: 'сек', upto: 'До' },
    ja: { starts: '開始まで', ends: '終了まで', soon: 'まもなく', d: '日', h: '時', m: '分', s: '秒', upto: '最大' },
    zh: { starts: '开始倒计时', ends: '结束倒计时', soon: '即将', d: '天', h: '时', m: '分', s: '秒', upto: '最高' },
    ar: { starts: 'يبدأ خلال', ends: 'ينتهي خلال', soon: 'قريباً', d: 'يوم', h: 'سا', m: 'دق', s: 'ث', upto: 'حتى' },
    hi: { starts: 'शुरू होने में', ends: 'समाप्त होने में', soon: 'जल्द', d: 'दिन', h: 'घंटे', m: 'मिनट', s: 'सेकंड', upto: 'तक' },
  };
  const tl = timerLabels[lang] || timerLabels.en;
  const timerLabel = isScheduled ? tl.starts : tl.ends;

  const isLanding = variant === 'landing';

  return (
    <>
      <style>{`
        @keyframes float-snow { 0% { transform: translateY(-10px) rotate(0deg); opacity: 0; } 50% { opacity: 1; } 100% { transform: translateY(60px) rotate(180deg); opacity: 0; } }
        @keyframes pulse-glow { 0%, 100% { opacity: 0.5; } 50% { opacity: 1; } }
        .promo-christmas .deco-1, .promo-winter .deco-1 { position: absolute; top: 8px; left: 10%; animation: float-snow 3s ease-in-out infinite; }
        .promo-christmas .deco-2, .promo-winter .deco-2 { position: absolute; top: 4px; right: 15%; animation: float-snow 4s ease-in-out infinite 1s; }
        .promo-christmas .deco-3, .promo-winter .deco-3 { position: absolute; top: 12px; left: 60%; animation: float-snow 3.5s ease-in-out infinite 0.5s; }
        .promo-halloween .deco-1 { position: absolute; top: 6px; left: 8%; animation: pulse-glow 2s ease-in-out infinite; }
        .promo-halloween .deco-2 { position: absolute; top: 6px; right: 12%; animation: pulse-glow 2.5s ease-in-out infinite 0.5s; }
        .promo-cyber .deco-1 { position: absolute; top: 5px; left: 5%; animation: pulse-glow 1.5s ease-in-out infinite; }
        .promo-cyber .deco-2 { position: absolute; top: 5px; right: 8%; animation: pulse-glow 2s ease-in-out infinite 0.3s; }
      `}</style>
      <div className={`relative overflow-hidden ${isLanding ? 'max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-4' : ''}`}>
        <div className={`relative overflow-hidden rounded-2xl ${theme.bg} border ${theme.border} p-5 sm:p-6 ${isLanding ? '' : 'mb-8'} ${theme.decoClass}`} data-testid="promo-banner">
          {/* Themed decorations */}
          {theme.decorations && (
            <>
              {templateKey === 'christmas' || templateKey === 'winter_sales' ? (
                <>
                  <Snowflake className="deco-1 w-4 h-4 text-white/20" />
                  <Snowflake className="deco-2 w-3 h-3 text-white/15" />
                  <Snowflake className="deco-3 w-3.5 h-3.5 text-white/10" />
                </>
              ) : templateKey === 'halloween' ? (
                <>
                  <Ghost className="deco-1 w-5 h-5 text-orange-400/20" />
                  <Ghost className="deco-2 w-4 h-4 text-purple-400/20" />
                </>
              ) : templateKey === 'cyber_monday' ? (
                <>
                  <Zap className="deco-1 w-4 h-4 text-cyan-400/20" />
                  <Zap className="deco-2 w-3.5 h-3.5 text-blue-400/20" />
                </>
              ) : null}
            </>
          )}

          <div className="flex items-center gap-4 relative z-10">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${theme.accent} bg-white/5 border border-white/10`}>
              <Icon className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-white font-bold text-base sm:text-lg">{promo.name}</h3>
                {isActive && (
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full bg-white/10 ${theme.accent}`}>
                    {tl.upto || 'Up to'} -{promo.discount_value}{promo.discount_type === 'percentage' ? '%' : '\u20ac'}
                  </span>
                )}
                {isScheduled && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400">{tl.soon}</span>
                )}
              </div>
              {promo.description && <p className="text-white/60 text-xs sm:text-sm mt-1">{(promo.descriptions && promo.descriptions[lang]) || promo.description}</p>}
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-white/40 text-[10px] uppercase tracking-wider mb-1">{timerLabel}</p>
              <div className="flex items-center gap-1.5">
                <div className="text-center"><span className={`font-heading text-lg sm:text-xl font-bold ${theme.accent} tabular-nums`}>{String(timeLeft.days).padStart(2,'0')}</span><span className="block text-white/30 text-[9px]">{tl.d}</span></div>
                <span className="text-white/20 text-sm font-light">:</span>
                <div className="text-center"><span className={`font-heading text-lg sm:text-xl font-bold ${theme.accent} tabular-nums`}>{String(timeLeft.hours).padStart(2,'0')}</span><span className="block text-white/30 text-[9px]">{tl.h || 'h'}</span></div>
                <span className="text-white/20 text-sm font-light">:</span>
                <div className="text-center"><span className={`font-heading text-lg sm:text-xl font-bold ${theme.accent} tabular-nums`}>{String(timeLeft.minutes).padStart(2,'0')}</span><span className="block text-white/30 text-[9px]">{tl.m || 'm'}</span></div>
                <span className="text-white/20 text-sm font-light">:</span>
                <div className="text-center"><span className={`font-heading text-lg sm:text-xl font-bold ${theme.accent} tabular-nums`}>{String(timeLeft.seconds).padStart(2,'0')}</span><span className="block text-white/30 text-[9px]">{tl.s || 's'}</span></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export function useActivePromo() {
  const [promo, setPromo] = useState(null);

  useEffect(() => {
    axios.get(`${API}/promo/active`).then(r => {
      if (r.data.promotions?.length > 0) setPromo(r.data.promotions[0]);
    }).catch(() => {});
  }, []);

  const getDiscountedPrice = (originalPrice, packageId) => {
    if (!promo) return null;
    if (promo.applicable_packages?.length > 0 && !promo.applicable_packages.includes(packageId)) return null;
    if (promo.discount_type === 'percentage') return Math.round(originalPrice * (1 - promo.discount_value / 100) * 100) / 100;
    return Math.max(Math.round((originalPrice - promo.discount_value) * 100) / 100, 0.50);
  };

  return { promo, getDiscountedPrice };
}
